import React, { useState, useEffect } from 'react';
import { usePWAInstall } from '../../hooks/usePWAInstall';
import { Download, X, Smartphone, Share, Plus, Home } from 'lucide-react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../hooks/AuthContext';

interface FirstTimeInstallModalProps {
  onDismiss?: () => void;
}

export const FirstTimeInstallModal: React.FC<FirstTimeInstallModalProps> = ({ onDismiss }) => {
  const { isInstallable, isInstalled, installApp } = usePWAInstall();
  const { user } = useAuth();
  const [show, setShow] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [step, setStep] = useState<'prompt' | 'ios-instructions'>('prompt');

  useEffect(() => {
    // Detect device type
    const userAgent = window.navigator.userAgent.toLowerCase();
    const iOS = /iphone|ipad|ipod/.test(userAgent);
    const mobile = /mobile|android|iphone|ipad|ipod/.test(userAgent);
    
    setIsIOS(iOS);
    setIsMobile(mobile);

    // Check if user has seen this prompt before
    const hasSeenInstallPrompt = localStorage.getItem('hasSeenInstallPrompt');
    const hasSeenFirstTimePrompt = localStorage.getItem('hasSeenFirstTimeInstallPrompt');
    
    // Show if:
    // 1. User is on mobile
    // 2. App is not already installed
    // 3. User hasn't seen this first-time prompt before
    // 4. Wait 2 seconds after page load for better UX
    if (mobile && !isInstalled && !hasSeenFirstTimePrompt) {
      const timer = setTimeout(() => {
        setShow(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isInstalled]);

  const handleDismiss = async () => {
    setShow(false);
    localStorage.setItem('hasSeenFirstTimeInstallPrompt', 'true');
    
    // Also mark in Firestore if user is logged in
    if (user?.uid) {
      try {
        const userRef = doc(db, 'users', user.uid);
        await updateDoc(userRef, {
          hasSeenInstallPrompt: true,
          installPromptShownAt: new Date().toISOString()
        });
      } catch (error) {
        console.error('Error updating user install prompt status:', error);
      }
    }
    
    onDismiss?.();
  };

  const handleInstall = async () => {
    if (isIOS) {
      // Show iOS instructions
      setStep('ios-instructions');
    } else if (isInstallable) {
      // Show native Android prompt
      try {
        const success = await installApp();
        if (success) {
          handleDismiss();
        } else {
          // If install didn't work, show fallback message
          alert('Unable to install the app. Please try adding it to your home screen manually from your browser menu (⋮ > Add to Home Screen).');
        }
      } catch (error) {
        console.error('Install error:', error);
        alert('Installation failed. Please try adding the app to your home screen from your browser menu (⋮ > Add to Home Screen).');
      }
    } else {
      // Not installable via prompt, show manual instructions
      alert('To install this app:\n\n1. Open your browser menu (⋮)\n2. Tap "Add to Home Screen"\n3. Tap "Add" to confirm');
      handleDismiss();
    }
  };

  const handleRemindLater = () => {
    setShow(false);
    // Don't set the hasSeenFirstTimeInstallPrompt, just close for now
    // This way it might show again next time they log in
    onDismiss?.();
  };

  if (!show || !isMobile) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black bg-opacity-60 backdrop-blur-sm animate-fadeIn">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        {step === 'prompt' ? (
          <>
            {/* Header */}
            <div className="bg-gradient-to-br from-red-600 to-orange-600 p-6 text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -translate-y-16 translate-x-16"></div>
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-3">
                  <div className="bg-white bg-opacity-20 p-3 rounded-xl">
                    <img 
                      src="/icons/ggg-logo-192.png" 
                      alt="GGG Logo" 
                      className="w-10 h-10 object-contain"
                    />
                  </div>
                  <button
                    onClick={handleDismiss}
                    className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <h2 className="text-2xl font-bold mb-2">
                  Install GGG App
                </h2>
                <p className="text-red-100 text-sm">
                  Get instant access from your home screen
                </p>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="bg-red-100 p-2 rounded-lg flex-shrink-0">
                    <Smartphone className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm">Quick Access</h3>
                    <p className="text-gray-600 text-xs">
                      Tap the home screen icon to open instantly
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="bg-blue-100 p-2 rounded-lg flex-shrink-0">
                    <Download className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm">Works Offline</h3>
                    <p className="text-gray-600 text-xs">
                      Access your tools even without internet
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="bg-green-100 p-2 rounded-lg flex-shrink-0">
                    <Home className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm">Full Screen</h3>
                    <p className="text-gray-600 text-xs">
                      Distraction-free experience, just like a native app
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-2 pt-2">
                <button
                  onClick={handleInstall}
                  className="w-full flex items-center justify-center space-x-2 bg-gradient-to-r from-red-600 to-orange-600 text-white font-semibold py-3 px-4 rounded-xl hover:from-red-700 hover:to-orange-700 transition-all transform hover:scale-105 shadow-lg active:scale-95"
                >
                  <Download className="w-5 h-5" />
                  <span>{isIOS ? 'Show Instructions' : (isInstallable ? 'Install Now' : 'Show Instructions')}</span>
                </button>
                
                <button
                  onClick={handleRemindLater}
                  className="w-full text-gray-600 hover:text-gray-900 font-medium py-2 text-sm"
                >
                  Remind me later
                </button>
                
                <button
                  onClick={handleDismiss}
                  className="w-full text-gray-400 hover:text-gray-600 text-xs"
                >
                  Don't show this again
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* iOS Instructions */}
            <div className="bg-gradient-to-br from-blue-600 to-indigo-600 p-6 text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -translate-y-16 translate-x-16"></div>
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-3">
                  <div className="bg-white bg-opacity-20 p-3 rounded-xl">
                    <Smartphone className="w-8 h-8" />
                  </div>
                  <button
                    onClick={handleDismiss}
                    className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <h2 className="text-2xl font-bold mb-2">
                  Install on iPhone/iPad
                </h2>
                <p className="text-blue-100 text-sm">
                  Follow these simple steps
                </p>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div className="space-y-4">
                {/* Step 1 */}
                <div className="flex items-start space-x-3">
                  <div className="bg-blue-600 text-white font-bold w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0">
                    1
                  </div>
                  <div className="flex-1">
                    <p className="text-gray-900 font-medium mb-2">
                      Tap the <span className="font-bold">Share</span> button
                    </p>
                    <div className="bg-gray-100 p-3 rounded-lg flex items-center space-x-2">
                      <Share className="w-5 h-5 text-blue-600" />
                      <span className="text-sm text-gray-700">
                        (At the bottom of Safari)
                      </span>
                    </div>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="flex items-start space-x-3">
                  <div className="bg-blue-600 text-white font-bold w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0">
                    2
                  </div>
                  <div className="flex-1">
                    <p className="text-gray-900 font-medium mb-2">
                      Scroll and tap <span className="font-bold">"Add to Home Screen"</span>
                    </p>
                    <div className="bg-gray-100 p-3 rounded-lg flex items-center space-x-2">
                      <Plus className="w-5 h-5 text-blue-600" />
                      <Home className="w-5 h-5 text-blue-600" />
                      <span className="text-sm text-gray-700">
                        Look for this icon
                      </span>
                    </div>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="flex items-start space-x-3">
                  <div className="bg-blue-600 text-white font-bold w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0">
                    3
                  </div>
                  <div className="flex-1">
                    <p className="text-gray-900 font-medium mb-2">
                      Tap <span className="font-bold">"Add"</span> to confirm
                    </p>
                    <p className="text-sm text-gray-600">
                      The GGG icon will appear on your home screen
                    </p>
                  </div>
                </div>
              </div>

              {/* Done Button */}
              <div className="pt-4">
                <button
                  onClick={handleDismiss}
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold py-3 px-4 rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg"
                >
                  Got it!
                </button>
              </div>

              <div className="text-center text-xs text-gray-500">
                Note: Installation is only available in Safari browser
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};



